/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { useState, useRef, useEffect } from 'react'
import { useRAGStore } from '../stores/rag-store'
import { useProjectStore } from '../stores/project-store'
import { Loader2, Send, Trash2, Database, FileText, FileDown, FileUp, X, Info, Zap, Search } from 'lucide-react'
import ReactMarkdown from 'react-markdown'

interface RAGChatProps {
  onHighlightFile?: (filePath: string) => void
  onOpenFile?: (filePath: string, highlightLines: { start: number; end: number }) => void
}

export function RAGChat({ onHighlightFile, onOpenFile }: RAGChatProps) {
  const [input, setInput] = useState('')
  const [model, setModel] = useState('openai/gpt-4o')
  const [selectedIndexId, setSelectedIndexId] = useState<string>('')
  const [showInfoModal, setShowInfoModal] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { currentProject, updateFile } = useProjectStore()
  const {
    messages,
    isIndexing,
    isQuerying,
    indexProgress,
    indexed,
    availableIndexes,
    currentIndexId,
    indexProject,
    askQuestion,
    clearMessages,
    exportCurrentIndex,
    importIndexFile,
    loadAvailableIndexes,
    loadIndex,
  } = useRAGStore()

  useEffect(() => {
    loadAvailableIndexes()

    // Check if user has seen the RAG info modal this session
    const hasSeenRAGInfo = sessionStorage.getItem('hasSeenRAGInfo')
    if (!hasSeenRAGInfo) {
      setShowInfoModal(true)
      sessionStorage.setItem('hasSeenRAGInfo', 'true')
    }
  }, [])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    // Update selected index when current index changes
    if (currentIndexId) {
      setSelectedIndexId(currentIndexId)
    }
  }, [currentIndexId])

  const handleIndex = async () => {
    if (!currentProject) return
    await indexProject(currentProject.id, currentProject.name, currentProject.files)
  }

  const handleSend = async () => {
    if (!input.trim() || !currentProject || isQuerying) return

    const question = input.trim()
    setInput('')
    await askQuestion(
      currentProject.id,
      question,
      model,
      currentProject.files,
      async (path: string, content: string) => {
        // Update the file in the project
        await updateFile(path, content)
        console.log('[RAG] File updated:', path)
      }
    )
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleExport = async () => {
    if (!currentProject) return
    await exportCurrentIndex(currentProject.id, currentProject.name)
  }

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    await importIndexFile(file)
    e.target.value = '' // Reset input
  }

  const handleIndexSelect = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const indexId = e.target.value
    setSelectedIndexId(indexId)

    if (indexId) {
      const indexData = availableIndexes.find(idx => idx.projectId === indexId)
      if (indexData) {
        await loadIndex(indexData)
      }
    }
  }

  if (!currentProject) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-foreground-muted">
          <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No project loaded</p>
          <p className="text-sm mt-2">Open or create a project to use RAG</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header - Single Row */}
      <div className="p-3 border-b border-border">
        <div className="flex items-center gap-2">
          {/* Title */}
          <div className="flex items-center gap-2 text-sm font-semibold whitespace-nowrap">
            <Database className="w-4 h-4" />
            <span>RAG</span>
          </div>

          {/* Model selector */}
          <select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="input text-xs px-2 py-1 flex-1 min-w-0"
            title="Select model"
          >
            <option value="openai/gpt-4o">GPT-4o</option>
            <option value="openai/gpt-4o-mini">GPT-4o Mini</option>
            <option value="anthropic/claude-3.5-sonnet">Claude 3.5 Sonnet</option>
            <option value="google/gemini-2.0-flash-exp">Gemini 2.0 Flash</option>
            <option value="google/gemini-2.5-pro">Gemini 2.5 Pro</option>
          </select>

          {/* Index selector */}
          <select
            value={selectedIndexId}
            onChange={handleIndexSelect}
            className="input text-xs px-2 py-1 flex-1 min-w-0"
            title="Select saved index"
          >
            <option value="">New Index</option>
            {availableIndexes.map(idx => {
              const date = new Date(idx.createdAt)
              const formattedDate = date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })
              return (
                <option key={idx.projectId} value={idx.projectId}>
                  {idx.projectName} - {formattedDate} ({idx.metadata.totalChunks} chunks)
                </option>
              )
            })}
          </select>

          {/* Index button */}
          <button
            onClick={handleIndex}
            disabled={isIndexing}
            className="btn-primary text-xs px-3 py-1 whitespace-nowrap"
            title={indexed ? "Re-index project" : "Index project"}
          >
            {isIndexing ? (
              <>
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                Indexing
              </>
            ) : (
              <>
                <Database className="w-3 h-3 mr-1" />
                {indexed ? 'Re-index' : 'Index'}
              </>
            )}
          </button>

          {/* Export button */}
          {indexed && (
            <button
              onClick={handleExport}
              className="btn-ghost px-2 py-1 text-xs"
              title="Export index to file"
            >
              <FileDown className="w-3 h-3" />
            </button>
          )}

          {/* Import button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="btn-ghost px-2 py-1 text-xs"
            title="Import index from file"
          >
            <FileUp className="w-3 h-3" />
          </button>

          {/* Clear chat button */}
          {messages.length > 0 && (
            <button
              onClick={clearMessages}
              className="btn-ghost px-2 py-1 text-xs"
              title="Clear chat"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>

        {/* Hidden file input for import */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleImport}
          className="hidden"
        />

        {/* Progress bar */}
        {indexProgress && (
          <div className="mt-2">
            <div className="flex justify-between text-xs text-foreground-muted mb-1">
              <span>Embedding chunks...</span>
              <span>{indexProgress.current}/{indexProgress.total}</span>
            </div>
            <div className="w-full bg-background-tertiary rounded-full h-1.5">
              <div
                className="bg-accent h-full rounded-full transition-all"
                style={{ width: `${(indexProgress.current / indexProgress.total) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && !indexed && (
          <div className="text-center text-foreground-muted mt-8">
            <Database className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-sm mb-2">Index your project to start asking questions</p>
            <p className="text-xs">RAG will create embeddings of your code for semantic search</p>
          </div>
        )}

        {messages.length === 0 && indexed && (
          <div className="text-center text-foreground-muted mt-8">
            <p className="text-sm mb-2">Ask questions about your codebase</p>
            <div className="text-xs space-y-1 mt-4">
              <p>• How does authentication work?</p>
              <p>• What are the main components?</p>
              <p>• Explain the database schema</p>
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className="space-y-2">
            {msg.role === 'user' && (
              <div className="bg-background-secondary rounded-lg p-3">
                <p className="text-sm font-semibold text-accent mb-1">You</p>
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              </div>
            )}

            {msg.role === 'assistant' && (
              <div className="bg-background-tertiary rounded-lg p-3">
                <p className="text-sm font-semibold text-foreground mb-2">Assistant</p>
                <div className="text-sm prose prose-invert max-w-none">
                  <ReactMarkdown>{msg.content}</ReactMarkdown>
                </div>
                
                {msg.citations && msg.citations.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <p className="text-xs text-foreground-muted mb-2">Citations:</p>
                    {msg.citations.map((cite, j) => (
                      <button
                        key={j}
                        onClick={() => {
                          // Highlight file in sidebar
                          onHighlightFile?.(cite.filePath)
                          // Open file with highlighted lines
                          onOpenFile?.(cite.filePath, { start: cite.startLine, end: cite.endLine })
                        }}
                        className="text-xs font-mono text-accent hover:text-accent/80 hover:underline mb-1 block text-left cursor-pointer"
                        title="Click to view file with highlighted lines"
                      >
                        {cite.filePath}:{cite.startLine}-{cite.endLine}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {msg.role === 'system' && (
              <div className="text-xs text-foreground-muted italic text-center">
                {msg.content}
              </div>
            )}
          </div>
        ))}

        {isQuerying && (
          <div className="flex items-center gap-2 text-sm text-foreground-muted">
            <Loader2 className="w-4 h-4 animate-spin" />
            Thinking...
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-border">
        <div className="flex gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={indexed ? "Ask a question..." : "Index project first..."}
            disabled={!indexed || isQuerying}
            className="input flex-1 text-sm resize-none"
            rows={2}
          />
          <button
            onClick={handleSend}
            disabled={!indexed || !input.trim() || isQuerying}
            className="btn-primary px-4"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-foreground-muted mt-1">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>

      {/* Info Modal */}
      {showInfoModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-4 border-b border-border flex items-center justify-between sticky top-0 bg-background">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Database className="w-5 h-5 text-accent" />
                Welcome to RAG Chat
              </h2>
              <button
                onClick={() => setShowInfoModal(false)}
                className="btn-ghost p-2"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* What is RAG */}
              <div>
                <h3 className="text-base font-semibold mb-2 flex items-center gap-2">
                  <Info className="w-4 h-4 text-accent" />
                  What is RAG?
                </h3>
                <p className="text-sm text-foreground-secondary leading-relaxed">
                  <strong className="text-foreground">RAG (Retrieval-Augmented Generation)</strong> enhances AI responses by searching your project folder for relevant context before answering questions. Instead of relying solely on the AI's training data, RAG finds the most relevant code snippets and includes them in the prompt, resulting in accurate, project-specific answers.
                </p>
              </div>

              {/* How it works */}
              <div>
                <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-accent" />
                  How It Works
                </h3>
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-accent/20 text-accent flex items-center justify-center text-xs font-bold">
                      1
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">Indexing</p>
                      <p className="text-xs text-foreground-secondary">
                        Your project files are split into chunks and converted into vector embeddings using OpenAI's embedding model. This creates a searchable index of your codebase.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-accent/20 text-accent flex items-center justify-center text-xs font-bold">
                      2
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">Searching</p>
                      <p className="text-xs text-foreground-secondary">
                        When you ask a question, it's converted to a vector and compared against your indexed code to find the most semantically similar chunks.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-accent/20 text-accent flex items-center justify-center text-xs font-bold">
                      3
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">Augmentation</p>
                      <p className="text-xs text-foreground-secondary">
                        The relevant code chunks are added to your question as context, giving the AI the exact information it needs to answer accurately.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Getting Started */}
              <div>
                <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
                  <Search className="w-4 h-4 text-accent" />
                  Getting Started
                </h3>
                <div className="bg-background-secondary rounded-lg p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 text-accent">▸</div>
                    <div className="text-sm">
                      <strong className="text-foreground">Click "Index"</strong> to create a searchable index of your project. This may take a few minutes for large projects.
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 text-accent">▸</div>
                    <div className="text-sm">
                      <strong className="text-foreground">Ask questions</strong> about your codebase like "How does authentication work?" or "What are the main components?"
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 text-accent">▸</div>
                    <div className="text-sm">
                      <strong className="text-foreground">Export/Import</strong> your index to save time - no need to re-index every session!
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 text-accent">▸</div>
                    <div className="text-sm">
                      <strong className="text-foreground">Switch indexes</strong> using the dropdown to work with different project versions.
                    </div>
                  </div>
                </div>
              </div>

              {/* Note */}
              <div className="bg-accent/10 border border-accent/30 rounded-lg p-4">
                <p className="text-xs text-foreground-secondary">
                  <strong className="text-accent">Note:</strong> Indexing requires an OpenAI API key for generating embeddings. The embedding model used is <code className="font-mono text-xs bg-background px-1 py-0.5 rounded">text-embedding-3-small</code>, which is cost-efficient at ~$0.02 per 1M tokens.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-border flex justify-end">
              <button
                onClick={() => setShowInfoModal(false)}
                className="btn-primary px-6"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

